import { Extension } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const headingRolesKey = new PluginKey<DecorationSet>('headingRoles');

/** htmldoc.ClassRoutesToNamedPage: the bare words only route a <section>. */
const routesToNamedPage = (node: ProseMirrorNode): boolean =>
  String(node.attrs.class ?? '')
    .split(/\s+/)
    .some(
      (name) =>
        name === 'cara-cover' ||
        name === 'cara-closing' ||
        ((name === 'cover' || name === 'closing') && node.type.name === 'section')
    );

/**
 * The renderer's htmldoc.MarkHeadingRoles: the highest heading level the body uses is role 1,
 * the next role 2, and so on. Cover and closing headings neither take a role nor set the scale.
 */
export const headingRoles = (doc: ProseMirrorNode): { pos: number; role: number }[] => {
  const headings: { pos: number; level: number }[] = [];
  doc.descendants((node, pos) => {
    if (routesToNamedPage(node)) return false;
    if (node.type.name !== 'heading') return true;
    headings.push({ pos, level: Number(node.attrs.level) });
    return false;
  });
  const top = Math.min(...headings.map((h) => h.level));
  return headings.map(({ pos, level }) => ({ pos, role: level - top + 1 }));
};

const buildDecorations = (doc: ProseMirrorNode): DecorationSet =>
  DecorationSet.create(
    doc,
    headingRoles(doc).map(({ pos, role }) =>
      Decoration.node(pos, pos + doc.nodeAt(pos)!.nodeSize, { 'data-cara-heading': String(role) })
    )
  );

/** Gives each heading the role the PDF styles it by, without writing it into the document. */
export const HeadingRoles = Extension.create({
  name: 'headingRoles',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: headingRolesKey,
        state: {
          init: (_, state) => buildDecorations(state.doc),
          apply: (tr, decorations) => (tr.docChanged ? buildDecorations(tr.doc) : decorations)
        },
        props: {
          decorations: (state) => headingRolesKey.getState(state)
        }
      })
    ];
  }
});
